import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import KsaRoadshowMap from '@/components/KsaRoadshowMap';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarDays, MapPin, CheckCircle2, PenLine } from 'lucide-react';

const CITY_OPTIONS = [
  { value: 'jeddah', label: 'Jeddah (Oct 26)' },
  { value: 'riyadh', label: 'Riyadh (Oct 27)' },
  { value: 'dammam', label: 'Dammam (Oct 28)' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
];

const PAM_EXPERTISE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'entry-level', label: 'Entry Level' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'expert', label: 'Expert' },
];

const TSHIRT_OPTIONS = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => ({ value: size, label: size }));

const INITIAL_FORM = {
  fullName: '',
  gender: '',
  workEmail: '',
  jobTitle: '',
  phone: '',
  companyName: '',
  city: '',
  pamExpertise: '',
  tshirtSize: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d{8,15}$/;

const WallixEventRegistration = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormInView, setIsFormInView] = useState(false);
  const formSectionRef = useRef(null);

  useEffect(() => {
    const el = formSectionRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsFormInView(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSelectChange = (name) => (value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!formData.fullName.trim()) next.fullName = 'Full name is required.';
    if (!formData.gender) next.gender = 'Please select a gender.';
    if (!formData.workEmail.trim()) next.workEmail = 'Work email is required.';
    else if (!EMAIL_RE.test(formData.workEmail.trim())) next.workEmail = 'Enter a valid email address.';
    if (!formData.jobTitle.trim()) next.jobTitle = 'Job title is required.';
    if (!formData.phone.trim()) next.phone = 'Phone number is required.';
    else if (!PHONE_RE.test(formData.phone.trim().replace(/[\s-]/g, ''))) next.phone = 'Enter a valid phone number (8–15 digits, optional +country code).';
    if (!formData.companyName.trim()) next.companyName = 'Company name is required.';
    if (!formData.city) next.city = 'Please select your participation city.';
    if (!formData.pamExpertise) next.pamExpertise = 'Please select your PAM expertise level.';
    if (!formData.tshirtSize) next.tshirtSize = 'Please select a t-shirt size.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validate()) {
      toast.error('Please fix the highlighted fields and try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('fullName', formData.fullName);
      fd.append('gender', formData.gender);
      fd.append('workEmail', formData.workEmail);
      fd.append('jobTitle', formData.jobTitle);
      fd.append('phone', formData.phone);
      fd.append('companyName', formData.companyName);
      fd.append('city', formData.city);
      fd.append('pamExpertise', formData.pamExpertise);
      fd.append('tshirtSize', formData.tshirtSize);
      fd.append('form_name', 'wallix-event-registration');

      const res = await fetch(`${process.env.PUBLIC_URL}/register-wallix-event.php`, {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {}

      if (!res.ok || !payload?.ok) {
        const msg = payload?.error || `Registration failed (${res.status})`;
        toast.error(msg);
        setIsSubmitting(false);
        return;
      }

      toast.success(payload.message || "Thanks! We've received your registration — check your inbox for a copy.");
      setIsSubmitted(true);
      setFormData(INITIAL_FORM);
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while submitting your registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Register: WALLIX Technical Partner Enablement Session | Gulf Digital"
        description="Join Gulf Digital and WALLIX for a hands-on Privileged Access Management technical enablement session touring Dammam, Riyadh, and Jeddah. Reserve your seat."
        url="/events/wallix-partner-enablement"
        ogTitle="WALLIX Technical Partner Enablement Session | Gulf Digital"
        ogDescription="A 3-city PAM technical enablement roadshow with WALLIX — Jeddah (Oct 26), Riyadh (Oct 27), Dammam (Oct 28). Register now."
        image="/assets/wallix.png"
      />
      <StructuredData
        type="Service"
        data={{
          '@type': 'Event',
          name: 'WALLIX Technical Partner Enablement Session',
          description:
            'A hands-on Privileged Access Management technical enablement roadshow co-hosted by Gulf Digital and WALLIX across Dammam, Riyadh, and Jeddah.',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
          organizer: { '@type': 'Organization', name: 'Gulf Digital', url: 'https://gulfdigital-me.com' },
          location: [
            { '@type': 'Place', name: 'Dammam, Saudi Arabia', address: { '@type': 'PostalAddress', addressLocality: 'Dammam', addressCountry: 'SA' } },
            { '@type': 'Place', name: 'Riyadh, Saudi Arabia', address: { '@type': 'PostalAddress', addressLocality: 'Riyadh', addressCountry: 'SA' } },
            { '@type': 'Place', name: 'Jeddah, Saudi Arabia', address: { '@type': 'PostalAddress', addressLocality: 'Jeddah', addressCountry: 'SA' } },
          ],
        }}
      />

      {/* Compact hero: logos, badge, title */}
      <section className="relative overflow-hidden py-8 md:py-10 border-b border-warm-gray-200" data-testid="event-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-warm-white via-warm-gray-50 to-warm-white" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src="/assets/logo.png" alt="Gulf Digital" className="h-12 w-auto" />
              <span className="text-warm-gray-300 text-xl font-light">×</span>
              <img src="/assets/wallix.png" alt="WALLIX" className="h-8 w-auto object-contain" />
            </div>
            <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-brand-maroon/10 text-brand-maroon font-medium text-sm mb-4">
              <CalendarDays className="w-4 h-4" />
              <span>Technical Partner Enablement Roadshow</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground leading-tight">
              WALLIX Technical Partner Enablement Session
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Two-column: intro/dates/map (shown first on mobile, left column on desktop) + form */}
      <section className="py-10 md:py-14 bg-white" data-testid="registration-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* Intro + dates + map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:order-1"
              data-testid="event-intro"
            >
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed text-center lg:text-left">
                Gulf Digital and WALLIX bring a hands-on Privileged Access Management enablement session to three cities across Saudi Arabia. Reserve your seat.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-8 text-sm font-semibold text-warm-gray-700">
                {CITY_OPTIONS.map((c) => (
                  <span key={c.value} className="inline-flex items-center gap-1.5 px-4 py-2 bg-warm-gray-50 border border-warm-gray-200 rounded-lg">
                    <MapPin className="w-3.5 h-3.5 text-brand-maroon" />
                    {c.label}
                  </span>
                ))}
              </div>
              <KsaRoadshowMap />
            </motion.div>

            {/* Registration form */}
            <div className="lg:order-2" ref={formSectionRef}>
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="border-2 border-brand-maroon/20 text-center">
                  <CardContent className="pt-10 pb-10">
                    <CheckCircle2 className="w-14 h-14 text-brand-maroon mx-auto mb-5" />
                    <h2 className="text-2xl font-bold mb-3">Registration Received!</h2>
                    <p className="text-muted-foreground mb-6">
                      Thank you for registering for the WALLIX Technical Partner Enablement Session. We've sent a copy to your email, and our team will review your registration and follow up shortly with confirmation, venue, and logistics details.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setIsSubmitted(false)}
                      data-testid="register-another-button"
                    >
                      Register Another Attendee
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card className="border-2 border-warm-gray-200">
                  <CardHeader>
                    <CardTitle className="text-2xl">Reserve Your Seat</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Fields marked * are required.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="event-registration-form" noValidate>
                      {/* Full Name */}
                      <div>
                        <Label htmlFor="fullName">Full Name *</Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="input-fullName"
                          aria-invalid={!!errors.fullName}
                        />
                        {errors.fullName && <p className="text-sm text-destructive mt-1">{errors.fullName}</p>}
                      </div>

                      {/* Gender */}
                      <div>
                        <Label htmlFor="gender">Gender *</Label>
                        <Select value={formData.gender} onValueChange={handleSelectChange('gender')}>
                          <SelectTrigger id="gender" className="mt-1" data-testid="select-gender" aria-invalid={!!errors.gender}>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            {GENDER_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.gender && <p className="text-sm text-destructive mt-1">{errors.gender}</p>}
                      </div>

                      {/* Work Email */}
                      <div>
                        <Label htmlFor="workEmail">Work Email *</Label>
                        <Input
                          id="workEmail"
                          name="workEmail"
                          type="email"
                          value={formData.workEmail}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="input-workEmail"
                          aria-invalid={!!errors.workEmail}
                        />
                        {errors.workEmail && <p className="text-sm text-destructive mt-1">{errors.workEmail}</p>}
                      </div>

                      {/* Job Title */}
                      <div>
                        <Label htmlFor="jobTitle">Job Title / Position *</Label>
                        <Input
                          id="jobTitle"
                          name="jobTitle"
                          value={formData.jobTitle}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="input-jobTitle"
                          aria-invalid={!!errors.jobTitle}
                        />
                        {errors.jobTitle && <p className="text-sm text-destructive mt-1">{errors.jobTitle}</p>}
                      </div>

                      {/* Phone */}
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+966 5X XXX XXXX"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="input-phone"
                          aria-invalid={!!errors.phone}
                        />
                        {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone}</p>}
                      </div>

                      {/* Company Name */}
                      <div>
                        <Label htmlFor="companyName">Company Name *</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="input-companyName"
                          aria-invalid={!!errors.companyName}
                        />
                        {errors.companyName && <p className="text-sm text-destructive mt-1">{errors.companyName}</p>}
                      </div>

                      {/* Participation City */}
                      <div>
                        <Label htmlFor="city">Participation City *</Label>
                        <Select value={formData.city} onValueChange={handleSelectChange('city')}>
                          <SelectTrigger id="city" className="mt-1" data-testid="select-city" aria-invalid={!!errors.city}>
                            <SelectValue placeholder="Select a city" />
                          </SelectTrigger>
                          <SelectContent>
                            {CITY_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.city && <p className="text-sm text-destructive mt-1">{errors.city}</p>}
                      </div>

                      {/* PAM Expertise */}
                      <div>
                        <Label htmlFor="pamExpertise">Technical Expertise in PAM Technology *</Label>
                        <Select value={formData.pamExpertise} onValueChange={handleSelectChange('pamExpertise')}>
                          <SelectTrigger id="pamExpertise" className="mt-1" data-testid="select-pamExpertise" aria-invalid={!!errors.pamExpertise}>
                            <SelectValue placeholder="Select expertise level" />
                          </SelectTrigger>
                          <SelectContent>
                            {PAM_EXPERTISE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.pamExpertise && <p className="text-sm text-destructive mt-1">{errors.pamExpertise}</p>}
                      </div>

                      {/* T-Shirt Size */}
                      <div>
                        <Label htmlFor="tshirtSize">T-Shirt Size *</Label>
                        <Select value={formData.tshirtSize} onValueChange={handleSelectChange('tshirtSize')}>
                          <SelectTrigger id="tshirtSize" className="mt-1" data-testid="select-tshirtSize" aria-invalid={!!errors.tshirtSize}>
                            <SelectValue placeholder="Select a size" />
                          </SelectTrigger>
                          <SelectContent>
                            {TSHIRT_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.tshirtSize && <p className="text-sm text-destructive mt-1">{errors.tshirtSize}</p>}
                      </div>

                      <input type="hidden" name="form_name" value="wallix-event-registration" />

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-brand-maroon hover:bg-brand-maroon-dark mt-2"
                        data-testid="submit-button"
                      >
                        {isSubmitting ? 'Submitting...' : 'Register Now'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile-only floating shortcut to the form, hidden once the form is on screen */}
      <button
        type="button"
        onClick={scrollToForm}
        className={`lg:hidden fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-brand-maroon px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-maroon/30 transition-all duration-300 hover:bg-brand-maroon-dark ${
          isFormInView ? 'opacity-0 translate-y-4 pointer-events-none' : 'opacity-100 translate-y-0'
        }`}
        data-testid="mobile-register-fab"
      >
        <PenLine className="w-4 h-4" />
        Register Now
      </button>
    </>
  );
};

export default WallixEventRegistration;
